import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getProjectById, getCategoryColor } from '../../data/projects';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = getProjectById(id);
  const [lightbox, setLightbox] = useState(null); // index or null

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);

  const prevImage = useCallback(() => {
    if (!project) return;
    setLightbox((i) => (i - 1 + project.images.length) % project.images.length);
  }, [project]);

  const nextImage = useCallback(() => {
    if (!project) return;
    setLightbox((i) => (i + 1) % project.images.length);
  }, [project]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, prevImage, nextImage]);

  const goToContact = (e) => {
    e.preventDefault();
    navigate('/#contact');
  };

  if (!project) {
    return (
      <div className="project-detail">
        <Navbar />
        <div className="project-detail__not-found container">
          <h1 className="project-detail__not-found-title">Proyecto no encontrado</h1>
          <p className="project-detail__not-found-text">
            El proyecto que buscas no existe o fue removido.
          </p>
          <Link to="/proyectos" className="project-detail__back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M13 8H3M3 8L8 13M3 8L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Volver a proyectos
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryColor = getCategoryColor(project.category);
  const isCompleted = project.status === 'Completado';

  const INFO_ROWS = [
    { key: 'Cliente', val: project.client },
    { key: 'Ubicación', val: project.location },
    { key: 'Área', val: project.area },
    { key: 'Año', val: project.year },
    { key: 'Duración', val: project.duration },
  ];

  return (
    <div className="project-detail">
      <Navbar />

      {/* Hero */}
      <div className="project-detail__hero">
        <img
          src={project.coverImage}
          alt={project.title}
          className="project-detail__hero-img"
          fetchpriority="high"
          decoding="async"
        />
        <div className="project-detail__hero-overlay" />
        <div className="project-detail__hero-content">
          <nav className="project-detail__breadcrumb" aria-label="Navegación">
            <Link to="/">Inicio</Link>
            <span className="project-detail__breadcrumb-sep">›</span>
            <Link to="/proyectos">Proyectos</Link>
            <span className="project-detail__breadcrumb-sep">›</span>
            <span className="project-detail__breadcrumb-current">{project.title}</span>
          </nav>
          <h1 className="project-detail__hero-title">{project.title}</h1>
          <div className="project-detail__hero-meta">
            <span
              className="project-detail__hero-badge"
              style={{ background: categoryColor }}
            >
              {project.category}
            </span>
            <span
              className={`project-detail__hero-status ${
                isCompleted
                  ? 'project-detail__hero-status--completed'
                  : 'project-detail__hero-status--in-process'
              }`}
            >
              {isCompleted ? '✓ Completado' : '◐ En proceso'}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="project-detail__body">
        <div className="container">
          <Link to="/proyectos" className="project-detail__back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M13 8H3M3 8L8 13M3 8L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Todos los proyectos
          </Link>

          <div className="project-detail__layout">
            {/* Main */}
            <div className="project-detail__main">
              {/* Description */}
              <div>
                <h2 className="project-detail__section-title">Descripción del proyecto</h2>
                <p className="project-detail__description">{project.fullDescription}</p>
              </div>

              {/* Highlights */}
              <div>
                <h2 className="project-detail__section-title">Aspectos destacados</h2>
                <div className="project-detail__highlights">
                  {project.highlights.map((h) => (
                    <div key={h} className="project-detail__highlight">
                      <span className="project-detail__highlight-dot" />
                      <span className="project-detail__highlight-text">{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gallery */}
              <div>
                <h2 className="project-detail__section-title">Galería fotográfica</h2>
                <div className="project-detail__gallery">
                  {project.images.map((img, idx) => (
                    <button
                      key={idx}
                      className="project-detail__gallery-btn"
                      onClick={() => openLightbox(idx)}
                      aria-label={`Ver imagen ${idx + 1} en grande`}
                    >
                      <img
                        src={img}
                        alt={`${project.title} — imagen ${idx + 1}`}
                        className="project-detail__gallery-img"
                        loading="lazy"
                        decoding="async"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="project-detail__sidebar">
              {/* Info card */}
              {!project.hideTechSheet && (
                <div className="project-detail__info-card">
                  <p className="project-detail__info-card-title">Ficha técnica</p>
                  <div className="project-detail__info-rows">
                    {INFO_ROWS.map((row) => (
                      <div key={row.key} className="project-detail__info-row">
                        <span className="project-detail__info-key">{row.key}</span>
                        <span className="project-detail__info-val">{row.val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="project-detail__cta-card">
                <h3 className="project-detail__cta-title">¿Proyecto similar?</h3>
                <p className="project-detail__cta-text">
                  Contáctenos para discutir su proyecto. Evaluación inicial 
                  gratuita y propuesta sin compromiso.
                </p>
                <button onClick={goToContact} className="project-detail__cta-btn">
                  Solicitar cotización
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H13M13 8L8 3M13 8L8 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <Footer />

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox__close" onClick={closeLightbox} aria-label="Cerrar">✕</button>
          <div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
            <img
              src={project.images[lightbox]}
              alt={`${project.title} — imagen ${lightbox + 1}`}
              className="lightbox__img"
            />
            <div className="lightbox__controls">
              <button className="lightbox__arrow" onClick={prevImage} aria-label="Anterior">‹</button>
              <span className="lightbox__counter">{lightbox + 1} / {project.images.length}</span>
              <button className="lightbox__arrow" onClick={nextImage} aria-label="Siguiente">›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
